import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ReactComponent as PlusIcon }    from '../../../assets/icons/PlusSquare.svg';
import { ReactComponent as RefreshIcon } from '../../../assets/icons/ArrowsCounterClockwise.svg';
import { ReactComponent as ExpandIcon }  from '../../../assets/icons/ArrowsOutIn.svg';
import api from '../../../api';
import ScheduleMeetingModal from '../../Calendar/ScheduleMeetingModal';
import ViewMeetingModal from '../../Meetings/ViewMeetingModal';
import { useI18n } from '../../../i18n';

/* ── Date helpers ── */
const getMonday = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
    d.setHours(0, 0, 0, 0);
    return d;
};

const toIsoDate = (date) => date.toISOString().slice(0, 10);

const toLocaleCode = (locale) => {
    if (locale === 'en') return 'en-GB';
    return locale || 'en';
};

const fmtShort = (date, locale) =>
    new Intl.DateTimeFormat(toLocaleCode(locale), { day: '2-digit', month: 'short', year: 'numeric' }).format(date);

const fmtTime = (iso, locale) => {
    if (!iso) return '';
    return new Intl.DateTimeFormat(toLocaleCode(locale), {
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date(iso));
};

const fmtMeetingDateTime = (meeting, locale) => {
    const iso = meeting.start_at;
    if (!iso) return '';
    const d = new Date(iso);
    return `${fmtShort(d, locale)}  ${fmtTime(iso, locale)}`;
};

/* ── Skeleton ── */
const CalSkeleton = () => (
    <div className="cal-skeleton">
        {[1, 2, 3].map(i => (
            <div key={i} className="cal-skel-item">
                <div className="cal-skel-title" />
                <div className="cal-skel-sub" />
            </div>
        ))}
    </div>
);

const VIEWS    = ['day', 'week', 'month', 'custom'];
const PER_PAGE = 10;

const CalendarWidget = () => {
    const { locale, t } = useI18n();

    const currentUserId = useMemo(() => {
        try {
            const u = JSON.parse(localStorage.getItem('user') || '{}');
            return u?.id || null;
        } catch (e) {
            return null;
        }
    }, []);

    const [viewMode, setViewMode]           = useState('week');
    const [refDate, setRefDate]             = useState(() => {
        const d = new Date(); d.setHours(0, 0, 0, 0); return d;
    });
    const [meetings, setMeetings]           = useState([]);
    const [page, setPage]                   = useState(1);
    const [hasMore, setHasMore]             = useState(false);
    const [loading, setLoading]             = useState(true);
    const [loadingMore, setLoadingMore]     = useState(false);
    const [isExpanded, setIsExpanded]       = useState(false);
    const [showSchedule, setShowSchedule]   = useState(false);
    const [selectedMeeting, setSelectedMeeting] = useState(null);
    const [customStart, setCustomStart]     = useState('');
    const [customEnd, setCustomEnd]         = useState('');
    const [error, setError]                 = useState(false);

    const compactBodyRef  = useRef(null);
    const expandedBodyRef = useRef(null);
    const abortRef        = useRef(null);
    const pageRef         = useRef(1);


    /* ── Range calculation ── */
    const getRange = useCallback(() => {
        if (viewMode === 'day') {
            const s = new Date(refDate); s.setHours(0, 0, 0, 0);
            const e = new Date(refDate); e.setHours(23, 59, 59, 999);
            return { start: s, end: e };
        }
        if (viewMode === 'week') {
            const s = getMonday(refDate);
            const e = new Date(s); e.setDate(e.getDate() + 6); e.setHours(23, 59, 59, 999);
            return { start: s, end: e };
        }
        if (viewMode === 'month') {
            const s = new Date(refDate.getFullYear(), refDate.getMonth(), 1);
            const e = new Date(refDate.getFullYear(), refDate.getMonth() + 1, 0, 23, 59, 59);
            return { start: s, end: e };
        }
        if (viewMode === 'custom' && customStart && customEnd) {
            if (customEnd < customStart) return null;
            return {
                start: new Date(customStart + 'T00:00:00'),
                end:   new Date(customEnd   + 'T23:59:59'),
            };
        }
        return null;
    }, [viewMode, refDate, customStart, customEnd]);

    /* ── Fetch ── */
    const fetchMeetings = useCallback(async (pageNum = 1, reset = true) => {
        const range = getRange();
        if (!range) { setMeetings([]); setLoading(false); return; }

        if (reset) {
            if (abortRef.current) abortRef.current.abort();
            abortRef.current = new AbortController();
            setLoading(true);
        } else {
            setLoadingMore(true);
        }

        setError(false);

        try {
            const res = await api.get('/meetings', {
                params: {
                    start: toIsoDate(range.start),
                    end:   toIsoDate(range.end),
                    page:       pageNum,
                    per_page:   PER_PAGE,
                },
                signal: abortRef.current?.signal,
            });

            if (res.status && res.data?.meetings) {
                const newItems = Array.isArray(res.data.meetings) ? res.data.meetings : [];
                setMeetings(prev => reset ? newItems : [...prev, ...newItems]);
                setPage(pageNum);
                pageRef.current = pageNum;
                setHasMore(res.data.pagination?.has_more ?? false);
            }
        } catch (err) {
            if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') {
                setError(true);
            }
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [getRange]);

    useEffect(() => { fetchMeetings(1, true); }, [fetchMeetings]);

    // Abort any in-flight request on unmount
    useEffect(() => () => { if (abortRef.current) abortRef.current.abort(); }, []);

    /* ── Infinite scroll ── */
    const handleScroll = useCallback((e) => {
        const el = e.currentTarget;
        if (loadingMore || !hasMore) return;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
            fetchMeetings(pageRef.current + 1, false);
        }
    }, [loadingMore, hasMore, fetchMeetings]);

    /* ── Navigate ── */
    const navigate = (dir) => {
        setRefDate(prev => {
            const d = new Date(prev);
            if (viewMode === 'day')   d.setDate(d.getDate() + dir);
            if (viewMode === 'week')  d.setDate(d.getDate() + dir * 7);
            if (viewMode === 'month') d.setMonth(d.getMonth() + dir);
            return d;
        });
    };

    const getViewLabel = () =>
        ({
            day: t('pages.calendar.widget.day'),
            week: t('pages.calendar.widget.week'),
            month: t('pages.calendar.widget.month'),
            custom: t('pages.calendar.widget.custom'),
        }[viewMode] || '');

    const getRangeLabel = () => {
        const r = getRange();
        if (!r) return t('legacy.select_date_range');
        if (viewMode === 'day') return fmtShort(r.start, locale);
        return `${fmtShort(r.start, locale)} - ${fmtShort(r.end, locale)}`;
    };

    /* ── Render pieces ── */
    const renderHeader = (onExpand) => (
        <div className="widget-header cal-header">
            <span className="widget-title cal-title">{t('navigation.calendar')}</span>
            <div className="header-right">
                <PlusIcon
                    className="widget-icon cal-icon"
                    onClick={() => setShowSchedule(true)}
                    title={t('pages.calendar.schedule_meeting')}
                />
                <RefreshIcon
                    className="widget-icon cal-icon"
                    onClick={() => fetchMeetings(1, true)}
                    title={t('common.actions.refresh')}
                />
                <ExpandIcon
                    className="widget-icon cal-icon"
                    onClick={onExpand}
                    title={isExpanded ? t('common.actions.collapse') : t('common.actions.expand')}
                />
            </div>
        </div>
    );

    const renderSubHeader = () => (
        <div className="cal-sub-header">
            <div className="cal-view-tabs">
                {VIEWS.map(v => (
                    <button
                        key={v}
                        className={`cal-tab ${viewMode === v ? 'active' : ''}`}
                        onClick={() => setViewMode(v)}
                    >
                        {t(`pages.calendar.widget.${v}`)}
                    </button>
                ))}
            </div>

            {viewMode === 'custom' ? (
                <div className="cal-custom-range">
                    <input
                        type="date" value={customStart}
                        onChange={e => setCustomStart(e.target.value)}
                        className="cal-date-input"
                    />
                    <span className="cal-range-sep">–</span>
                    <input
                        type="date" value={customEnd}
                        onChange={e => setCustomEnd(e.target.value)}
                        className="cal-date-input"
                    />
                </div>
            ) : (
                <div className="cal-nav">
                    <button className="cal-nav-btn" onClick={() => navigate(-1)}>‹</button>
                    <div className="cal-range-label">
                        <span className="cal-view-label">{getViewLabel()}</span>
                        <span className="cal-range-text">{getRangeLabel()}</span>
                    </div>
                    <button className="cal-nav-btn" onClick={() => navigate(1)}>›</button>
                </div>
            )}
        </div>
    );

    const renderBody = (ref) => (
        <div className="widget-body cal-body" ref={ref} onScroll={handleScroll}>
            {loading ? (
                <CalSkeleton />
            ) : error ? (
                <div className="cal-empty">{t('pages.calendar.errors.load_failed')}</div>
            ) : meetings.length === 0 ? (
                <div className="cal-empty">
                    {viewMode === 'custom' && (!customStart || !customEnd)
                        ? t('legacy.select_start_end_date')
                        : viewMode === 'custom' && customEnd < customStart
                        ? t('pages.calendar.widget.end_date_after_start')
                        : t('legacy.no_meetings_period')}
                </div>
            ) : (
                <>
                    {meetings.map(meeting => (
                        <div
                            key={meeting.id}
                            className="cal-meeting-item"
                            onClick={() => setSelectedMeeting(meeting)}
                        >
                            <div className="cal-meeting-title">{meeting.title}</div>
                            <div className="cal-meeting-time">
                                {fmtMeetingDateTime(meeting, locale)}
                            </div>
                        </div>
                    ))}
                    {loadingMore && (
                        <div className="cal-load-more">
                            <div className="cal-load-spinner" />
                        </div>
                    )}
                </>
            )}
        </div>
    );

    return (
        <>
            {/* ── Compact widget ── */}
            <div className="widget-card cal-widget">
                {renderHeader(() => setIsExpanded(true))}
                {renderSubHeader()}
                {renderBody(compactBodyRef)}
            </div>

            {/* ── Expanded overlay ── */}
            {isExpanded && (
                <div className="nfw-expanded-overlay" onClick={() => setIsExpanded(false)}>
                    <div className="nfw-expanded-panel" onClick={e => e.stopPropagation()}>
                        {renderHeader(() => setIsExpanded(false))}
                        {renderSubHeader()}
                        {renderBody(expandedBodyRef)}
                    </div>
                </div>
            )}

            {/* ── Schedule meeting modal ── */}
            <ScheduleMeetingModal
                isOpen={showSchedule}
                onClose={() => setShowSchedule(false)}
                onSuccess={() => { setShowSchedule(false); fetchMeetings(1, true); }}
            />

            {/* ── Meeting details modal ── */}
            {selectedMeeting && (
                <ViewMeetingModal
                    isOpen={true}
                    meeting={selectedMeeting}
                    onClose={() => setSelectedMeeting(null)}
                    currentUserId={currentUserId}
                    onUpdate={() => fetchMeetings(1, true)}
                />
            )}
        </>
    );
};

export default CalendarWidget;
